import React from 'react';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { Loader2, CalendarPlus, User } from 'lucide-react';
import { getShortDayName } from '../../lib/utils';

export function BulkCreateDialog({
  open,
  onClose,
  onSubmit,
  shiftTemplates,
  staff,
  weekDates,
  bulkFormData,
  setBulkFormData,
  creatingBulk,
  toggleUser,
  toggleDate,
  selectAllStaff,
  selectAllDates,
}) {
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CalendarPlus size={18} />
            Bulk Create Shifts
          </DialogTitle>
          <DialogDescription>
            Apply a shift template to multiple employees across multiple days at once.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto space-y-4 py-2">
          {/* Template selector */}
          <div className="space-y-1.5">
            <Label className="text-sm font-medium">Shift Template</Label>
            <Select
              value={bulkFormData.template_id}
              onValueChange={(val) => setBulkFormData(p => ({ ...p, template_id: val }))}
            >
              <SelectTrigger data-testid="bulk-template-select">
                <SelectValue placeholder="Select a template" />
              </SelectTrigger>
              <SelectContent>
                {shiftTemplates.map((t) => (
                  <SelectItem key={t.id} value={t.id}>
                    <div className="flex items-center gap-2">
                      <span
                        className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                        style={{ backgroundColor: t.role_color || t.color || '#94a3b8' }}
                      />
                      <span>{t.name}</span>
                      <span className="text-xs text-slate-400">{t.start_time}–{t.end_time}</span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Employee selector */}
          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <Label className="text-sm font-medium">
                Employees
                {bulkFormData.user_ids.length > 0 && (
                  <span className="ml-1.5 text-xs font-normal text-slate-500">
                    ({bulkFormData.user_ids.length} selected)
                  </span>
                )}
              </Label>
              <Button
                variant="ghost"
                size="sm"
                className="text-xs h-6 px-2"
                onClick={selectAllStaff}
                data-testid="bulk-select-all-staff"
              >
                Select All
              </Button>
            </div>
            <div className="border border-slate-200 rounded-lg max-h-36 overflow-y-auto divide-y divide-slate-100">
              {staff.map((member) => (
                <label
                  key={member.id}
                  className="flex items-center gap-3 px-3 py-2 hover:bg-slate-50 cursor-pointer"
                  data-testid={`bulk-staff-${member.id}`}
                >
                  <input
                    type="checkbox"
                    className="rounded accent-orange-500"
                    checked={bulkFormData.user_ids.includes(member.id)}
                    onChange={() => toggleUser(member.id)}
                  />
                  <div className="flex items-center gap-2">
                    <User size={14} className="text-slate-400" />
                    <span className="text-sm text-slate-800">{member.name}</span>
                  </div>
                </label>
              ))}
            </div>
          </div>

          {/* Day picker */}
          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <Label className="text-sm font-medium">
                Days
                {bulkFormData.dates.length > 0 && (
                  <span className="ml-1.5 text-xs font-normal text-slate-500">
                    ({bulkFormData.dates.length} selected)
                  </span>
                )}
              </Label>
              <Button
                variant="ghost"
                size="sm"
                className="text-xs h-6 px-2"
                onClick={selectAllDates}
                data-testid="bulk-select-all-dates"
              >
                Select All
              </Button>
            </div>
            <div className="grid grid-cols-7 gap-1">
              {weekDates.map((date, i) => {
                const dateStr = date.toISOString().split('T')[0];
                const isSelected = bulkFormData.dates.includes(dateStr);
                const isToday = date.toDateString() === new Date().toDateString();
                return (
                  <button
                    key={i}
                    type="button"
                    onClick={() => toggleDate(date)}
                    className={`flex flex-col items-center py-1.5 px-1 rounded border text-xs transition-colors ${
                      isSelected
                        ? 'bg-orange-500 border-orange-500 text-white'
                        : isToday
                          ? 'border-orange-300 text-orange-600 hover:bg-orange-50'
                          : 'border-slate-200 text-slate-600 hover:bg-slate-50'
                    }`}
                    data-testid={`bulk-date-${dateStr}`}
                  >
                    <span className="font-medium">{getShortDayName(date.getDay()).slice(0, 2)}</span>
                    <span className="mt-0.5 opacity-80">{date.getDate()}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Draft toggle */}
          <div className="flex items-center justify-between p-3 bg-slate-50 border border-slate-200 rounded-lg">
            <div>
              <p className="text-sm font-medium text-slate-900">Save as draft</p>
              <p className="text-xs text-slate-500">Drafts are not visible to employees until published</p>
            </div>
            <Switch
              checked={bulkFormData.is_draft}
              onCheckedChange={(val) => setBulkFormData(p => ({ ...p, is_draft: val }))}
              data-testid="bulk-draft-toggle"
            />
          </div>

          {/* Preview summary */}
          {bulkFormData.template_id && bulkFormData.user_ids.length > 0 && bulkFormData.dates.length > 0 && (
            <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-800">
              <span className="font-medium">
                {bulkFormData.user_ids.length * bulkFormData.dates.length}
              </span>{' '}
              shift{bulkFormData.user_ids.length * bulkFormData.dates.length !== 1 ? 's' : ''} will be created
              {bulkFormData.is_draft ? ' as drafts' : ' and published immediately'}.
            </div>
          )}
        </div>

        <div className="flex gap-3 pt-2 border-t border-slate-100">
          <Button variant="outline" className="flex-1" onClick={onClose} disabled={creatingBulk}>
            Cancel
          </Button>
          <Button
            className="flex-1"
            onClick={onSubmit}
            disabled={
              creatingBulk ||
              !bulkFormData.template_id ||
              bulkFormData.user_ids.length === 0 ||
              bulkFormData.dates.length === 0
            }
            data-testid="bulk-create-confirm-btn"
          >
            {creatingBulk ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <CalendarPlus size={14} className="mr-2" />
            )}
            Create Shifts
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
